import { ErrorHandler, Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LanguageService } from '@core/i18n/language.service';
import { ToastService } from '../components/ui/toast/toast';

export interface ErrorOptions {
  message: string;
  duration?: number;
  action?: string;
}

export interface ErrorModalOptions {
  message: string;
  title?: string;
}

/**
 * Signal-based error service for handling and displaying errors
 * Replaces the RxJS-based ErrorService
 *
 * Usage:
 * ```typescript
 * const errorService = inject(ErrorService);
 *
 * // Show toast error
 * errorService.showSnackbar('Error message', 3000);
 *
 * // Show modal error
 * errorService.showModal('Error message', 'Error Title');
 *
 * // Handle API error
 * errorService.handleApiError(error);
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  private readonly ravenService = inject(ErrorHandler);
  private readonly toastService = inject(ToastService);
  private readonly languageService = inject(LanguageService);

  // State signals for modals
  private readonly _modalOptions = signal<ErrorModalOptions | null>(null);
  private readonly _unauthModal = signal<string | null>(null);

  // Public read-only signals
  readonly modalOptions = this._modalOptions.asReadonly();
  readonly unauthModal = this._unauthModal.asReadonly();

  // Computed signals for checking if modals are active
  readonly hasModal = computed(() => this._modalOptions() !== null);
  readonly hasUnauthModal = computed(() => this._unauthModal() !== null);

  /**
   * Show a toast error notification
   * Uses ToastService to display error messages
   */
  showSnackbar(message: string, duration?: number, action?: string): void {
    this.toastService.show(
      {
        title: message,
        variant: 'error',
        action: action
          ? {
              label: action,
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onClick: () => {},
            }
          : undefined,
      },
      {
        duration: duration,
      }
    );
  }

  /**
   * Show an error modal
   */
  showModal(message: string, title: string = 'There was an issue.', options: { report?: boolean } = {}): void {
    const { report = true } = options;
    if (report) {
      this.ravenService.handleError(message);
    }
    this._modalOptions.set({ message, title });
  }

  /**
   * Clear modal
   */
  clearModal(): void {
    this._modalOptions.set(null);
  }

  /**
   * Show unauthorized modal
   */
  showUnauthModal(): void {
    this._unauthModal.set('');
  }

  /**
   * Clear unauthorized modal
   */
  clearUnauthModal(): void {
    this._unauthModal.set(null);
  }

  /**
   * Handle API error response
   * Automatically shows toast with error message
   * Uses console.error directly to avoid circular dependency with Logger
   *
   * When the response contains a structured error code (`body.code`), attempts
   * to resolve a translated message from `errors.codes.{code}` using `body.data`
   * as interpolation params. Falls back to `body.message` or a generic fallback.
   */
  handleApiError(err: unknown): void {
    const fallback = this.languageService.translate('messages.errorOccurred');
    try {
      const body = this.extractErrorBody(err);
      const translated = this.translateBody(body);
      if (translated) {
        this.showSnackbar(translated);
        return;
      }

      const message = typeof body['message'] === 'string' ? body['message'] : null;
      this.showSnackbar(message ?? fallback);
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error('[API Error] Failed to parse error response', ex, String(err));
      this.showSnackbar(fallback);
    }
  }

  /**
   * Resolve a structured backend error `code` to a localized message via `errors.codes.{code}`,
   * forwarding the backend's `data` as interpolation params.
   *
   * Returns null when there is no code or no translation for it, so callers can fall back to the
   * backend's own message. Callers that reach the API through `BaseApiService` get this for free via
   * `handleApiError`; routes that bypass it (raw `HttpClient` calls, e.g. the payments endpoints) must
   * call this directly from their own error handling.
   */
  translateErrorCode(code: string | null | undefined, data?: Record<string, unknown>): string | null {
    if (!code) {
      return null;
    }
    const key = `errors.codes.${code}`;
    const translated = this.languageService.translate(key, data ?? {});
    return translated === key ? null : translated;
  }

  /**
   * Resolve a caught error straight to its localized `errors.codes.{code}` message,
   * applying the same body/type guards `handleApiError` uses.
   *
   * For callers that reach the API without `BaseApiService` (raw `HttpClient`/`authHttp`,
   * e.g. the claim-details endpoints) and so never get `handleApiError` — they raise their
   * own toast, and this gives them the coded message without re-implementing the extraction.
   * Returns null when there is no code, no translation, or the body cannot be parsed —
   * so the caller can fall back to the backend's own message. Never throws: callers run
   * this as the first statement of an error handler, where a throw would strand the rest
   * of their cleanup (a loading flag left set, a toast never raised).
   */
  translateErrorFrom(err: unknown): string | null {
    try {
      return this.translateBody(this.extractErrorBody(err));
    } catch (ex) {
      // Same reporting as `handleApiError`'s catch, and console rather than Logger
      // for the same reason (circular dependency). Swallowing this silently would
      // make a broken translation look identical to "no code on the body".
      // eslint-disable-next-line no-console
      console.error('[API Error] Failed to translate error code', ex, String(err));
      return null;
    }
  }

  /**
   * Resolve an already-extracted error body to its `errors.codes.{code}` message.
   * Shared by `handleApiError` and `translateErrorFrom` so the `code`/`data` type
   * guards cannot drift apart — the two entry points must agree on which bodies
   * count as coded.
   */
  private translateBody(body: Record<string, unknown>): string | null {
    const code = typeof body['code'] === 'string' ? body['code'] : null;
    const data =
      typeof body['data'] === 'object' && body['data'] !== null ? (body['data'] as Record<string, unknown>) : undefined;
    return this.translateErrorCode(code, data);
  }

  private extractErrorBody(err: unknown): Record<string, unknown> {
    if (err instanceof HttpErrorResponse) {
      const requestId = err.headers?.get('X-Request-ID') ?? null;
      const body: Record<string, unknown> = typeof err.error === 'object' && err.error !== null ? err.error : {};

      if (typeof body['devMessage'] === 'string') {
        // eslint-disable-next-line no-console
        console.error('[API Error]', {
          message: body['devMessage'],
          statusCode: typeof body['statusCode'] === 'number' ? body['statusCode'] : err.status,
          requestId,
          timestamp: new Date().toISOString(),
        });
      }
      return body;
    }
    return typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : {};
  }

  /**
   * Handle generic error
   */
  handleError(error: Error | string): void {
    const message = error instanceof Error ? error.message : error;
    this.ravenService.handleError(message);
    this.showSnackbar(message);
  }
}
